import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSearch, 
  faFilter, 
  faSort, 
  faEye, 
  faReply, 
  faChartBar,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faInbox,
  faTimes,
  faEdit,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './FeedbackManagementPage.css';

const FeedbackManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // State management
  const [feedbacks, setFeedbacks] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    feedbackType: '',
    priority: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  
  // Modal states
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseData, setResponseData] = useState({
    status: '',
    adminResponse: '',
    priority: ''
  });
  
  // Constants for dropdowns
  const statusOptions = {
    '': 'All Statuses',
    'SUBMITTED': 'Submitted',
    'IN_REVIEW': 'In Review',
    'IN_PROGRESS': 'In Progress',
    'RESOLVED': 'Resolved',
    'CLOSED': 'Closed',
    'REJECTED': 'Rejected'
  };
  
  const priorityOptions = {
    '': 'All Priorities',
    'LOW': 'Low',
    'MEDIUM': 'Medium',
    'HIGH': 'High',
    'CRITICAL': 'Critical'
  };
  
  const feedbackTypeOptions = {
    '': 'All Types',
    'BUG_REPORT': 'Bug Report',
    'FEATURE_REQUEST': 'Feature Request',
    'GENERAL_FEEDBACK': 'General Feedback',
    'TECHNICAL_ISSUE': 'Technical Issue',
    'ACCOUNT_ISSUE': 'Account Issue',
    'PAYMENT_ISSUE': 'Payment Issue',
    'EVENT_ISSUE': 'Event Issue',
    'UI_UX_FEEDBACK': 'UI/UX Feedback',
    'PERFORMANCE_ISSUE': 'Performance Issue',
    'OTHER': 'Other'
  };

  // Fetch feedback list with filters and pagination
  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: currentPage,
        size: pageSize,
        sort: `${sortBy},${sortDir}`
      });
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          queryParams.append(key, value.trim());
        }
      });
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/feedback?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch feedback');
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setFeedbacks(data.data.content || []);
        setTotalPages(data.data.totalPages || 1);
        setTotalElements(data.data.totalElements || 0);
      } else {
        throw new Error(data.message || 'Failed to load feedback');
      }
      
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, currentPage, pageSize, sortBy, sortDir, filters]);

  // Fetch feedback statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/feedback/statistics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setStatistics(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, [getAccessToken]);

  // Initial load
  useEffect(() => {
    fetchFeedbacks();
    fetchStatistics();
  }, [fetchFeedbacks, fetchStatistics]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters (reset to first page)
  const applyFilters = () => {
    setCurrentPage(0);
    fetchFeedbacks();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      feedbackType: '',
      priority: ''
    });
    setCurrentPage(0);
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setCurrentPage(0);
  };

  // View feedback details
  const viewFeedback = async (feedbackId) => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/feedback/${feedbackId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setSelectedFeedback(data.data);
          setShowDetailModal(true);
        }
      }
    } catch (err) {
      console.error('Error fetching feedback details:', err);
      setError('Failed to fetch feedback details');
    }
  };

  // Open response modal
  const openResponseModal = (feedback) => {
    setSelectedFeedback(feedback);
    setResponseData({
      status: feedback.status || 'IN_REVIEW',
      adminResponse: feedback.adminResponse || '',
      priority: feedback.priority || 'MEDIUM'
    });
    setShowResponseModal(true);
  };

  // Update feedback
  const updateFeedback = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/feedback/${selectedFeedback.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(responseData)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setSuccessMessage('Feedback updated successfully!');
          setShowResponseModal(false);
          fetchFeedbacks(); // Refresh the list
          fetchStatistics(); // Refresh statistics
          setTimeout(() => setSuccessMessage(''), 5000);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update feedback');
      }
    } catch (err) {
      console.error('Error updating feedback:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Retry failed emails
  const retryFailedEmails = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      
      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/feedback/retry-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setSuccessMessage('Email retry initiated successfully!');
        fetchStatistics(); // Refresh statistics
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Error retrying emails:', err);
      setError('Failed to retry emails');
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    // If it's a number (Unix timestamp in seconds), multiply by 1000 to get milliseconds
    const timestamp = typeof dateString === 'number' ? dateString * 1000 : dateString;
    return new Date(timestamp).toLocaleString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL': return '#dc3545';
      case 'HIGH': return '#fd7e14';
      case 'MEDIUM': return '#ffc107';
      case 'LOW': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUBMITTED': return '#17a2b8';
      case 'IN_REVIEW': return '#ffc107';
      case 'IN_PROGRESS': return '#fd7e14';
      case 'RESOLVED': return '#28a745';
      case 'CLOSED': return '#6c757d';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="feedback-management-container">
      <div className="page-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>
        <h2>Feedback Management</h2>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Statistics Dashboard */}
      {statistics && (
        <div className="statistics-dashboard">
          <h3><FontAwesomeIcon icon={faChartBar} /> Feedback Statistics</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon submitted">
                <FontAwesomeIcon icon={faInbox} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{statistics.statusStats?.SUBMITTED || 0}</div>
                <div className="stat-label">New Submissions</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon in-progress">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{statistics.unresolvedCount || 0}</div>
                <div className="stat-label">Unresolved</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon resolved">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{statistics.statusStats?.RESOLVED || 0}</div>
                <div className="stat-label">Resolved</div>
              </div>
            </div>
            
            {statistics.failedEmailCount > 0 && (
              <div className="stat-card">
                <div className="stat-icon failed">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{statistics.failedEmailCount}</div>
                  <div className="stat-label">Failed Emails</div>
                  <button 
                    onClick={retryFailedEmails}
                    className="retry-emails-btn"
                    title="Retry failed email notifications"
                  >
                    <FontAwesomeIcon icon={faSync} /> Retry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <button 
          className="toggle-filters-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} /> 
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        
        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <input
                  type="text"
                  name="search"
                  placeholder="Search feedback..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="search-input"
                />
              </div>
              
              <div className="filter-group">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  {Object.entries(statusOptions).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <select
                  name="feedbackType"
                  value={filters.feedbackType}
                  onChange={handleFilterChange}
                >
                  {Object.entries(feedbackTypeOptions).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <select
                  name="priority"
                  value={filters.priority}
                  onChange={handleFilterChange}
                >
                  {Object.entries(priorityOptions).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-actions">
                <button onClick={applyFilters} className="apply-filters-btn">
                  <FontAwesomeIcon icon={faSearch} /> Apply
                </button>
                <button onClick={resetFilters} className="reset-filters-btn">
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Table */}
      <div className="feedback-table-container">
        {loading ? (
          <div className="loading-indicator">Loading feedback...</div>
        ) : feedbacks.length > 0 ? (
          <>
            <div className="table-header">
              <div className="results-count">
                Showing {feedbacks.length} of {totalElements} feedback items
              </div>
              <div className="page-size-selector">
                <label>Items per page:</label>
                <select 
                  value={pageSize} 
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            
            <table className="feedback-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID
                    {sortBy === 'id' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('userEmail')} className="sortable">
                    User
                    {sortBy === 'userEmail' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('feedbackType')} className="sortable">
                    Type
                    {sortBy === 'feedbackType' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('subject')} className="sortable">
                    Subject
                    {sortBy === 'subject' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('priority')} className="sortable">
                    Priority
                    {sortBy === 'priority' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    Status
                    {sortBy === 'status' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th onClick={() => handleSort('createdAt')} className="sortable">
                    Created
                    {sortBy === 'createdAt' && (
                      <FontAwesomeIcon 
                        icon={faSort} 
                        className={`sort-icon ${sortDir}`} 
                      />
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr key={feedback.id} className="feedback-row">
                    <td className="feedback-id">#{feedback.id}</td>
                    <td className="user-info">
                      <div className="user-email">{feedback.userEmail}</div>
                      <div className="ibo-number">IBO: {feedback.iboNumber}</div>
                    </td>
                    <td className="feedback-type">
                      <span className={`type-badge ${feedback.feedbackType.toLowerCase()}`}>
                        {feedbackTypeOptions[feedback.feedbackType] || feedback.feedbackType}
                      </span>
                    </td>
                    <td className="subject">
                      <div className="subject-text" title={feedback.subject}>
                        {feedback.subject}
                      </div>
                    </td>
                    <td className="priority">
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(feedback.priority) }}
                      >
                        {priorityOptions[feedback.priority] || feedback.priority}
                      </span>
                    </td>
                    <td className="status">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(feedback.status) }}
                      >
                        {statusOptions[feedback.status] || feedback.status}
                      </span>
                    </td>
                    <td className="created-date">
                      {formatDate(feedback.createdAt)}
                    </td>
                    <td className="actions">
                      <button 
                        onClick={() => viewFeedback(feedback.id)}
                        className="action-btn view-btn"
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button 
                        onClick={() => openResponseModal(feedback)}
                        className="action-btn respond-btn"
                        title="Respond to feedback"
                      >
                        <FontAwesomeIcon icon={faReply} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="pagination-btn"
                >
                  First
                </button>
                <button 
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="pagination-info">
                  Page {currentPage + 1} of {totalPages}
                </span>
                
                <button 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="pagination-btn"
                >
                  Next
                </button>
                <button 
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="pagination-btn"
                >
                  Last
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-feedback">
            <p>No feedback found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <div className="modal-overlay">
          <div className="modal-content detail-modal">
            <div className="modal-header">
              <h3>Feedback Details</h3>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="close-modal-btn"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-row">
                  <label>ID:</label>
                  <span>#{selectedFeedback.id}</span>
                </div>
                <div className="detail-row">
                  <label>User Email:</label>
                  <span>{selectedFeedback.userEmail}</span>
                </div>
                <div className="detail-row">
                  <label>IBO Number:</label>
                  <span>{selectedFeedback.iboNumber}</span>
                </div>
                <div className="detail-row">
                  <label>Type:</label>
                  <span className={`type-badge ${selectedFeedback.feedbackType.toLowerCase()}`}>
                    {feedbackTypeOptions[selectedFeedback.feedbackType]}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Priority:</label>
                  <span 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(selectedFeedback.priority) }}
                  >
                    {priorityOptions[selectedFeedback.priority]}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(selectedFeedback.status) }}
                  >
                    {statusOptions[selectedFeedback.status]}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Created:</label>
                  <span>{formatDate(selectedFeedback.createdAt)}</span>
                </div>
                {selectedFeedback.updatedAt && (
                  <div className="detail-row">
                    <label>Updated:</label>
                    <span>{formatDate(selectedFeedback.updatedAt)}</span>
                  </div>
                )}
              </div>
              
              <div className="detail-section">
                <label>Subject:</label>
                <div className="detail-content">{selectedFeedback.subject}</div>
              </div>
              
              <div className="detail-section">
                <label>Description:</label>
                <div className="detail-content description">
                  {selectedFeedback.description}
                </div>
              </div>
              
              {selectedFeedback.adminResponse && (
                <div className="detail-section">
                  <label>Admin Response:</label>
                  <div className="detail-content admin-response">
                    {selectedFeedback.adminResponse}
                  </div>
                  {selectedFeedback.responseBy && (
                    <div className="response-meta">
                      Responded by: {selectedFeedback.responseBy} 
                      {selectedFeedback.respondedAt && (
                        <span> on {formatDate(selectedFeedback.respondedAt)}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {(selectedFeedback.userAgent || selectedFeedback.deviceInfo || selectedFeedback.ipAddress) && (
                <div className="technical-info">
                  <h4>Technical Information</h4>
                  {selectedFeedback.userAgent && (
                    <div className="tech-row">
                      <label>User Agent:</label>
                      <span>{selectedFeedback.userAgent}</span>
                    </div>
                  )}
                  {selectedFeedback.deviceInfo && (
                    <div className="tech-row">
                      <label>Device Info:</label>
                      <span>{selectedFeedback.deviceInfo}</span>
                    </div>
                  )}
                  {selectedFeedback.ipAddress && (
                    <div className="tech-row">
                      <label>IP Address:</label>
                      <span>{selectedFeedback.ipAddress}</span>
                    </div>
                  )}
                </div>
              )}
              
              {selectedFeedback.attachments && (
                <div className="detail-section">
                  <label>Attachments:</label>
                  <div className="attachments">
                    {selectedFeedback.attachments.split(',').map((url, index) => (
                      <a 
                        key={index} 
                        href={url.trim()} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="attachment-link"
                      >
                        Attachment {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => openResponseModal(selectedFeedback)}
                className="respond-btn"
              >
                <FontAwesomeIcon icon={faEdit} /> Respond to Feedback
              </button>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedFeedback && (
        <div className="modal-overlay">
          <div className="modal-content response-modal">
            <div className="modal-header">
              <h3>Respond to Feedback</h3>
              <button 
                onClick={() => setShowResponseModal(false)}
                className="close-modal-btn"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="feedback-summary">
                <h4>Feedback Summary</h4>
                <p><strong>From:</strong> {selectedFeedback.userEmail}</p>
                <p><strong>Subject:</strong> {selectedFeedback.subject}</p>
                <p><strong>Type:</strong> {feedbackTypeOptions[selectedFeedback.feedbackType]}</p>
              </div>
              
              <div className="response-form">
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={responseData.status}
                    onChange={(e) => setResponseData(prev => ({ ...prev, status: e.target.value }))}
                  >
                    {Object.entries(statusOptions).slice(1).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    value={responseData.priority}
                    onChange={(e) => setResponseData(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    {Object.entries(priorityOptions).slice(1).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="adminResponse">Admin Response</label>
                  <textarea
                    id="adminResponse"
                    rows={6}
                    value={responseData.adminResponse}
                    onChange={(e) => setResponseData(prev => ({ ...prev, adminResponse: e.target.value }))}
                    placeholder="Enter your response to the user..."
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={updateFeedback}
                className="update-btn"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Feedback'}
              </button>
              <button 
                onClick={() => setShowResponseModal(false)}
                className="cancel-btn"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagementPage; 